// The HOME-BOARD half of the keyboard map (HOME_AMBIENT_SKIN_SPEC §5):
// 1–9 open the nth app tile, J / K walk the focus ring, A runs the focused
// widget's square action, E edits the board,
// Shift + an arrow resizes the focused widget in edit mode
// (DESIGN-WL-HOME-WIDTH-RESIZE-1 — `registerHomeResizeShortcuts` below, armed
// from registerDefaultShortcuts).
//
// THE BOARD IS THE SURFACE only while no PageSlot app window covers it — the
// rule `homeContextMenuAllowed` (functions/homeContextMenu.js) already applies
// to the board's right-click: `_canvasSlot` / `_canvasSlotSecond` open ⇒ the
// board does not answer.
//
// ⚠ THE DOM READS (isolated here, on purpose). The board is the CSS grid of
// pages/main.js (`MainColumn.GridContainer`, class `home-grid`,
// DESIGN-WL-HOME-WIDGETS-1). Every card is ONE HomeWidgetFrame element — the
// grid item and the card (components/HomeWidgetFrame.js) — whose STATE ROW is
// the widget descriptor `{ id, kind, openRoute, … }` and whose CSS `order` is
// its index in `root._homeLayout`: the grid flows that order, so it IS the
// reading order (DOM order is not). The app shortcuts are not cards: they sit
// in the App strip (components/HomeAppStrip.js, components/HomeAppRail.js),
// already in that order. Two readers:
//   • `homeBoardCards` — the grid element through the element tree, its DOM
//     children (every plane's frames: the static ones, and the module /
//     pending fragments render straight into the grid), each child's `ref`
//     (the smbls back-link to its frame element) and the frame's keyboard
//     entry, the title button (`Header.TitleRow.Title`);
//   • `homeStripTiles` — the strip's glass capsule (the Dock's
//     `MainColumn.HomeAppStrip.DockCapsule`, or the Rail's
//     `HomeAppRail.RailCapsule` beside the board — the person's look), its DOM
//     children and each child's `ref` to its item (state row + `Open`, the
//     item's button); "All apps" carries no id and is not a numbered tile.
// No `document.querySelector*`, no writes — every other export here asks these
// two.

const _shell = (origin) =>
  (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin

const _shortcutsRootOf = (host) => {
  const st = host && host.state
  return (st && st.root) || st
}

const _boardPart = (host, key) => {
  const page = host && host.content
  const column = page && page.MainColumn
  const part = column && column[key]
  return part && part.node ? part : null
}
const _boardGrid = (host) => _boardPart(host, 'GridContainer')

// The assistant's generated board over the grid area (Page mode): the grid is
// NOT the surface while it shows — its cards are inert under the view
// (functions/homeWidgetFrame.js homeWidgetUnderView), so J / K and E pass them
// by. The App strip above the grid stays live (1–9). The view's own rule,
// read from the registry: a shell without the assistant reads false.
const _underView = (host, root) => {
  const fns = host && host.context && host.context.functions
  const open = fns && fns.assistantBoardOpen
  return typeof open === 'function' && !!open.call(host, root)
}

export function homeBoardActive() {
  const host = _shell(this)
  const root = _shortcutsRootOf(host)
  if (!host || !root || root._canvasSlot || root._canvasSlotSecond) return false
  if (_underView(host, root)) return false
  return !!_boardGrid(host)
}

// The board's cards in READING order (the saved layout order), hidden widgets
// left out: [{ id, card, node, focusNode, spec, route, focusable }].
// `node` is the card (a control inside it counts as the card); `focusNode` is
// the one control the focus ring lands on.
export function homeBoardCards() {
  const host = _shell(this)
  const root = _shortcutsRootOf(host)
  if (!host || !root || root._canvasSlot || root._canvasSlotSecond) return []
  if (_underView(host, root)) return []
  const grid = _boardGrid(host)
  const kids = grid && grid.node && grid.node.children
  if (!kids || !kids.length) return []
  const hidden =
    root._homeHiddenWidgets && typeof root._homeHiddenWidgets === 'object'
      ? root._homeHiddenWidgets
      : {}
  const layout = Array.isArray(root._homeLayout) ? root._homeLayout : []
  const place = (id) => {
    const at = layout.findIndex((e) => e && e.id === id)
    return at < 0 ? layout.length : at
  }
  const out = []
  Array.prototype.forEach.call(kids, (node, dom) => {
    const card = node && node.ref
    const row = card && card.state
    const id = row && row.id
    if (!id || node.isConnected === false || hidden[id]) return
    const header = card.Header
    const entry = header && header.TitleRow && header.TitleRow.Title
    const focusNode = (entry && entry.node) || null
    out.push({
      id,
      card,
      node,
      focusNode,
      spec: row,
      route: row.openRoute || null,
      focusable: !!focusNode,
      at: place(id),
      dom
    })
  })
  out.sort((a, b) => a.at - b.at || a.dom - b.dom)
  return out
}

// J / K — move the focus ring to the next / previous focusable card. Focus
// inside a card (a control in its body) counts as that card; from nowhere, J
// lands on the first card and K on the last; the ends hold.
export function focusHomeCard(step, event) {
  const cards = (this.call('homeBoardCards') || []).filter((c) => c.focusable)
  if (!cards.length) return false
  const t = event && event.target
  const at = t
    ? cards.findIndex(
        (c) => c.node === t || (typeof c.node.contains === 'function' && c.node.contains(t))
      )
    : -1
  const next =
    at < 0 ? (step > 0 ? 0 : cards.length - 1) : Math.min(cards.length - 1, Math.max(0, at + step))
  const node = cards[next].focusNode || cards[next].node
  try {
    node.focus({ focusVisible: true })
  } catch (_) {
    node.focus()
  }
  return true
}

// The App strip's numbered tiles, in the strip's order (already the layout
// order): [{ id, item, button, spec }]. Same surface rule as the cards.
// The strip is the person's LOOK (functions/homeLook.js `homeLookRead`): the
// Dock — its glass capsule in the main column
// (`MainColumn.HomeAppStrip.DockCapsule`) — or the Rail beside the board
// (`HomeAppRail.RailCapsule`). Both render the SAME rows in the same order
// (pages/main.js `_appStripChildren`) as the capsule's own DOM children (their
// `Items` is a fragment), so the tiles are the capsule of the person's style,
// the other one when that one is not mounted. The strip turned off: no
// numbered tiles, the digit stays the browser's. Before, this read the Dock
// ROW's children — the capsule itself, no id — so 1–9 opened nothing once the
// strip became a capsule (measured on dev 07faf3568, 2026-09-29).
export function homeStripTiles() {
  const host = _shell(this)
  const root = _shortcutsRootOf(host)
  if (!host || !root || root._canvasSlot || root._canvasSlotSecond) return []
  let look = null
  try {
    look = host.call('homeLookRead', root)
  } catch (_) {
    look = null
  }
  if (look && look.stripShown === false) return []
  const page = host.content
  const railEl = page && page.HomeAppRail
  const rail = railEl && railEl.RailCapsule
  const dockRow = _boardPart(host, 'HomeAppStrip')
  const dock = dockRow && dockRow.DockCapsule
  const mounted = (part) => !!(part && part.node && part.node.children && part.node.children.length)
  const capsule =
    look && look.stripStyle === 'rail'
      ? mounted(rail)
        ? rail
        : dock
      : mounted(dock)
        ? dock
        : rail
  const kids = capsule && capsule.node && capsule.node.children
  if (!kids || !kids.length) return []
  const out = []
  Array.prototype.forEach.call(kids, (node) => {
    const item = node && node.ref
    const row = item && item.state
    const button = item && item.Open
    if (!row || !row.id || !button || node.isConnected === false) return
    out.push({ id: row.id, item, button, spec: row })
  })
  return out
}

// 1–9 — open the nth app tile of the strip through the tile's OWN path: its
// button runs `homeWidgetOpen(row, event)` (the zoom origin from the tile, the
// open hook, then `openAppPage` — functions/homeWidgetFrame.js), so the key
// and the click can never drift. The parity is pinned against the tile's
// shipped click in __tests__/shortcutsDefault.test.js.
export function openHomeTile(n, event) {
  const tile = (this.call('homeStripTiles') || [])[Number(n) - 1]
  if (!tile) return false
  tile.button.call('homeWidgetOpen', tile.spec, event || null)
  return true
}

// Shift + ← → ↑ ↓ — step the FOCUSED widget one size that way (edit mode):
// the card the keystroke came from (focus inside it — the title button J / K
// land on, or any control of the card) steps through
// functions/homeWidgetResize.js `homeWidgetResizeStepBy`, the same path as the
// arrows on the card's own resize handle. False — the key stays the
// browser's — when no card holds the focus or no size lies that way.
export function homeWidgetKeyResize(event) {
  const e = event
  if (!e) return false
  const dir =
    e.key === 'ArrowRight'
      ? 'right'
      : e.key === 'ArrowLeft'
        ? 'left'
        : e.key === 'ArrowDown'
          ? 'down'
          : e.key === 'ArrowUp'
            ? 'up'
            : null
  if (!dir) return false
  const t = e.target
  if (!t) return false
  const hit = this.call('homeCardOfTarget', t)
  if (!hit || !hit.card || typeof hit.card.call !== 'function') return false
  return !!hit.card.call('homeWidgetResizeStepBy', hit.spec, dir, hit.card)
}

// The board card a keystroke came from — focus ON the card or INSIDE it (the
// title button J / K land on, or any control of the card): its
// `homeBoardCards` entry, else null. With no target, the card that holds the
// keyboard focus — a row's `when` has no event, and asks this way.
export function homeCardOfTarget(target) {
  const cards = this.call('homeBoardCards') || []
  let t = target
  if (!t && cards.length) {
    const doc = cards[0].node && cards[0].node.ownerDocument
    t = doc && doc.activeElement
  }
  if (!t) return null
  return (
    cards.find(
      (c) => c.node === t || (typeof c.node.contains === 'function' && c.node.contains(t))
    ) || null
  )
}

// A — the FOCUSED widget's square action (HOME_AMBIENT_SKIN_SPEC §5, the
// frame's ONE square button — components/HomeWidgetFrame.js
// `Header.HomeWidgetAction`: the widget's declared verb, ＋ Add contact / New
// deal, or ↗ Open). The key presses that button through its OWN declared
// `onClick`, with the key event, so the key and the click can never drift
// (1–9 and Esc press their buttons the same way). False — the key stays the
// browser's — when no card holds the focus or the card shows no action
// (`homeWidgetActionOf` answers none: the button is hidden).
export function homeWidgetKeyAction(event) {
  const hit = this.call('homeCardOfTarget', event && event.target)
  const header = hit && hit.card && hit.card.Header
  const btn = header && header.HomeWidgetAction
  if (!btn || typeof btn.onClick !== 'function' || typeof btn.call !== 'function') return false
  if (!btn.call('homeWidgetActionOf', hit.spec)) return false
  const ev = event || { preventDefault() {}, stopPropagation() {} }
  btn.onClick(ev, btn, btn.state)
  return true
}

// The Shift + arrow rows — one row in the ? sheet ("Resize the selected
// widget", ⇧ ← → ↑ ↓), live in edit mode on the board. Armed by
// registerDefaultShortcuts (functions/shortcutsDefault.js), so the registry
// stays one listener.
export function registerHomeResizeShortcuts() {
  const el = this
  el.call('registerShortcut', {
    id: 'resizeWidget',
    keys: ['shift+arrowright', 'shift+arrowleft', 'shift+arrowdown', 'shift+arrowup'],
    keycaps: ['⇧', '←', '→', '↑', '↓'],
    label: 'shell.shortcuts.row.resizeWidget',
    group: 'shell.headerTabs.dashboard',
    groupOrder: 3,
    order: 7,
    when: (h, root) =>
      !!(root && root.signedIn && root._homeCustomizeMode) && !!h.call('homeBoardActive'),
    run: (h, root, e) => h.call('homeWidgetKeyResize', e)
  })
}
